// The group "year so far", as a mountable figure.
//
// Lives here rather than in a page because it has two homes and they are not
// the same audience: a teacher opens it on their class from /teacher, a district
// admin opens it on a school or the whole district from the Learning page of
// District Insights. The two differ in scope, in chrome and in what a sentence
// underneath should say — and in nothing about the figure itself, so the figure
// itself is the thing that moves.
//
// What it does NOT do is fetch. Both hosts already own a scope, a window and a
// focus cycle, and a component that fetched would either duplicate that
// machinery or fight it. It takes the payload.
//
// It also no longer carries a standard-by-standard margin under the timeline.
// That panel answered "which standards is this group stuck on", and it was the
// weakest available answer to it at every scale: on a district or a school the
// Learning page's By-curriculum lens splits the same mastery by unit on a page
// built for the question, and for one class the Classroom Matrix grid says it
// per learner AND covers the standards nobody has opened, which a margin over
// attempted standards structurally cannot. Two hundred rows of bar chart to be
// beaten by both neighbours was not worth the scroll.
import React from "react";
import {
  Card,
  Footnote,
  Legend,
  PanelHead,
  PlotNote,
  RailStat,
  StatRail,
} from "../../impact/ui";
import { stateLegend, relLegend, NEUTRAL } from "./palette";
import { GradePlacement, hasPlacement } from "./placement";
import { GrowthTimeline } from "./timeline";

// Whether there is anything to draw. Exported so a host can pick between this
// and its own empty state without reaching into the payload's shape.
const groupHasData = (data) =>
  !!(data && data.mode === "group" && data.totals && data.totals.pairs > 0);

const GroupYearSoFar = ({
  data,
  // What the charts are about, in words: "Ms. Alvarez's class", "Sunset Park
  // Elementary", a district's name. The headings and the footnote read off this
  // so a school-wide chart never calls itself a class.
  population,
  shade = true,
  // Narrowing already applied to the payload, named only so the footnote can
  // say so. The figure does not filter anything itself.
  subject = null,
  // One sentence the host adds to the footnote — where to go from here differs
  // per host, and it is the only thing about this figure that does.
  hint = null,
  // Off where the host's own navigation already names this view: on the
  // Learning page the lens tab says "Trajectory" three lines above, and a
  // heading repeating it is a heading that tells the reader nothing. The
  // subtitle, which names the population, stays either way.
  titled = true,
}) => {
  if (!groupHasData(data)) return null;
  const totals = data.totals;
  const perLearner = totals.learners
    ? (totals.by_state.outright + totals.by_state.assisted) / totals.learners
    : 0;
  const per = data.interval === "week" ? "week" : "month";
  // The slope, which is the one number a time axis carries and a composition
  // does not. Denominated in the buckets the group was actually MOVING in --
  // first entry to last -- rather than in the window's, so a district that
  // stopped in March is not credited with four quiet months of pace it never
  // worked. Falls back to the whole window if the series is somehow empty,
  // which `groupHasData` has already ruled out.
  const active = data.series
    ? data.series.reduce(
        (acc, b, i) =>
          b.entered && Object.values(b.entered).some((v) => v > 0)
            ? { first: acc.first === null ? i : acc.first, last: i }
            : acc,
        { first: null, last: 0 }
      )
    : { first: null, last: 0 };
  const spans =
    active.first === null ? (data.buckets || []).length || 1
                          : active.last - active.first + 1;
  const pace = totals.learners
    ? (totals.by_state.outright + totals.by_state.assisted) /
      totals.learners /
      spans
    : 0;
  const placed = hasPlacement(totals.placement);

  return (
    <>
      {/* The same figure as one learner's, off the same fold — a group's
          numbers are its learners' numbers added up, so the two charts cannot
          tell different stories. What changes is that a band now counts
          learner-standard pairs, which is why the rail above it carries the
          per-learner rate: a stock this size says as much about how many
          learners the group holds as about how they are doing. */}
      <PanelHead
        title={titled ? "Trajectory" : null}
        sub={`Every learner in ${population}, on one axis.`}
      />
      {/* Above the plot, because it is the plot's caption in numbers: how many
          learners are on this axis, how fast the stock is growing, and where
          the work sits relative to the grades these learners are in. The three
          mastery counts that used to sit here are gone -- outright / assisted /
          still open are exactly what the By-curriculum lens is FOR, and
          repeating them here spent the strip restating a neighbouring tab
          instead of saying what only a time axis can. */}
      <Card style={{ marginBottom: 16 }}>
        <StatRail min={150}>
          <RailStat
            label="Learners working"
            value={totals.learners}
            sub={`of ${totals.roster} on the roster`}
          />
          <RailStat
            label="Per learner"
            value={perLearner.toFixed(1)}
            sub="standards mastered, per learner who worked"
          />
          <RailStat
            label={`Pace, per ${per}`}
            value={pace.toFixed(1)}
            sub={`standards per learner over ${spans} active ${per}${
              spans === 1 ? "" : "s"
            }`}
          />
          <RailStat
            label="Standards touched"
            value={totals.standards}
            sub={`across ${totals.strands} strand${
              totals.strands === 1 ? "" : "s"
            }`}
          />
        </StatRail>
        {/* The caption takes a quarter and the bars the rest: at the rail's own
            auto-fit sizing the two subjects landed against the right edge with
            a column of white between them and their own label. */}
        {placed && (
          <StatRail
            divided
            style={{ gridTemplateColumns: "minmax(200px, 1fr) 3fr" }}
          >
            <RailStat
              label="Where they're working"
              sub={
                "% of learners, placed by the average level of their own " +
                "work — one column per " + per + ", everything up to it"
              }
            />
            <GradePlacement
              placement={totals.placement}
              series={data.series}
              buckets={data.buckets}
              interval={data.interval}
            />
          </StatRail>
        )}
      </Card>

      <Legend items={[...stateLegend(), ...(shade ? relLegend(NEUTRAL) : [])]} />
      <Card style={{ marginBottom: 20, overflowX: "auto" }}>
        <GrowthTimeline data={data} shade={shade} />
        <PlotNote
          details={
            "Above the axis, every standard any learner has mastered; below " +
            "it, every one attempted and still open. A standard is counted " +
            "once per learner, so a class of 24 all working the same standard " +
            "is 24 — the shape is the group's progress, not the curriculum's " +
            "coverage, and the rail above carries the per-learner rate for " +
            "comparing groups of different sizes. A standard enters in the " +
            "band it was reached in: one mastered on contact was never 'not " +
            "yet'. Texture is the grade channel, judged per learner against " +
            "the grade they held in this window — a group spans several " +
            "grades, so the same standard can be above grade for one learner " +
            "and at grade for another. Note which population that texture " +
            "counts: standards, one per learner. The rail above splits the " +
            "same four bands over LEARNERS, which is why it is drawn as a " +
            "constant-height share and this is drawn as counts — a class " +
            "where one learner races ahead through twenty above-grade " +
            "standards looks very different in the two."
          }
        >
          Standing on top, arriving underneath — standards, one per learner,
          one {per} per column.
        </PlotNote>
      </Card>

      <Footnote>
        {totals.learners} of {totals.roster} learner
        {totals.roster === 1 ? "" : "s"} in {population} worked{" "}
        {totals.standards} standard{totals.standards === 1 ? "" : "s"} in{" "}
        {data.standardset}
        {subject ? ` · ${subject === "MATH" ? "math" : "reading"} only` : ""}.{" "}
        {hint ? `${hint} ` : ""}
        Mastery is counted inside the selected school year, so one mastered last
        year reads as not-yet until it is worked again.
      </Footnote>
    </>
  );
};

export { GroupYearSoFar, groupHasData };
